import { type ApiProblem, type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import type { StaffLocale } from '@rental/domain'
import {
  COUNTER_PAYMENT_METHODS,
  type CounterPaymentMethod,
  customerName,
  ENDED_WITHOUT_RENTAL,
  TAKES_COUNTER_PAYMENT,
} from '@rental/domain'
import {
  countryName,
  format,
  formatDate,
  formatDateTime,
  formatEuro,
  formatTime,
  localized,
  MESSAGES,
  plural,
  type StaffMessages,
} from '@rental/i18n'
import { Button, buttonVariants, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { type FormEvent, type ReactNode, useId, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api } from '../api'
import { SecondDriverSection } from '../booking/SecondDriverSection'
import { type CanDo, canDo } from '../booking-can'
import { bookingQuery } from '../booking-query'
import { DrawerField } from '../cash/DrawerField'
import { rememberedPoint } from '../cash/drawer'
import { refreshDrawersAfter } from '../cash/queries'
import { placeName, StatusBadge } from '../components/BookingItem'
import { ChoiceChips } from '../components/ChoiceChips'
import { MoneyStrip } from '../components/MoneyStrip'
import { OutcomeFocus, OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { PendingLine } from '../components/PendingBadge'
import { ArrivalNotice } from '../components/Said'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { ReasonList, SubPanel } from '../components/SubPanel'
import { TechnicalLines } from '../components/TechnicalDetails'
import {
  EmailText,
  Facts,
  FIELD,
  FIELD_ROW,
  Loading,
  ProblemNotice,
  StaffInput,
} from '../components/ui'
import { AnonymisedNote, ErasedOn } from '../customer/Erased'
import { BookingTrips } from '../deliveries/BookingTrips'
import { DepositPanel } from '../deposit/DepositPanel'
import { DocumentList } from '../documents/Documents'
import { reasonText } from '../eligibility-words'
import { FineMentions } from '../fines/FineMentions'
import { MoneyDocuments, refreshFiscal } from '../fiscal/Documents'
import {
  type DocumentDraft,
  defaultKind,
  documentBody,
  newDraft,
  withKind,
} from '../fiscal/document'
import { FiscalFields } from '../fiscal/FiscalFields'
import { holdState } from '../hold'
import { useLocale } from '../i18n'
import { InspectionPanel } from '../inspection/InspectionPanel'
import {
  CloseHint,
  EndingFacts,
  LifecycleActions,
  LifecycleNotices,
  type LifecycleOutcome,
} from '../lifecycle/LifecycleActions'
import { centsForInput, parseEuroCents, paymentNote } from '../money'
import { problemOf } from '../problems'
import { lineLabel } from '../quote-lines'
import { RefundPanel } from '../refunds/Refunds'
import { fuelLabel } from '../rental'
import { ContractPdfLink } from '../signing/ContractPdfLink'
import { MoveBookingAction } from './MoveBooking'

const route = getRouteApi('/bookings/$code')

type Detail = ApiSchemas['StaffBookingDetail']

/**
 * W2-S S4: a website draft waits for its online share. Nothing here confirms
 * it, hands it over or takes its money: only the payment does. It can be
 * cancelled (fee nothing), from the actions.
 */
function HoldNotice({ hold }: { hold: Detail['booking']['hold'] }) {
  const { locale, messages: m } = useLocale()
  const held = holdState(hold, new Date())
  if (!hold || !held) return null
  return (
    <Notice tone={held.kind === 'held' ? 'info' : 'warning'}>
      <p>
        {held.kind === 'held'
          ? format(m.hold.heldNote, {
              amount: formatEuro(hold.prepayCents, locale),
              time: formatTime(locale, held.until),
            })
          : m.hold.endedNote}
      </p>
    </Notice>
  )
}

export function BookingScreen() {
  const { locale, messages: m } = useLocale()
  const { code } = route.useParams()
  const { created, done } = route.useSearch()
  const detail = useQuery(bookingQuery(code))
  const problem = problemOf(detail.error)
  const notFound = problem?.kind === 'api' && (problem.status === 404 || problem.status === 400)
  const back = (
    <Link to="/bookings" className="flex min-h-touch items-center gap-1 self-start">
      <ChevronLeft aria-hidden="true" className="size-4" />
      {m.booking.back}
    </Link>
  )

  if (notFound) {
    return (
      <>
        <PageHeader back={back} title={format(m.booking.title, { code: code.toUpperCase() })} />
        {/* Said as it comes, as a record's (UI-2g). */}
        <Notice tone="warning" live="assertive">
          <p>{m.booking.notFound}</p>
        </Notice>
      </>
    )
  }
  if (!detail.data) {
    // The title waits for the booking, so that it is focused once there is one to read.
    return (
      <>
        <div className="glass-pane flex flex-col gap-2 rounded-card p-4 sm:p-6">
          {back}
          {problem ? null : <Loading />}
        </div>
        <ProblemNotice problem={problem} />
      </>
    )
  }
  return (
    <BookingDetail
      detail={detail.data}
      locale={locale}
      m={m}
      back={back}
      notices={
        <>
          <ProblemNotice problem={problem} />
          {created ? (
            <ArrivalNotice>
              <p>{format(m.booking.created, { code: detail.data.booking.code })}</p>
            </ArrivalNotice>
          ) : null}
          {done ? (
            <ArrivalNotice>
              <p>
                {done === 'checkout'
                  ? m.rental.handedOver
                  : done === 'return'
                    ? m.rental.takenBack
                    : m.paper.done}
              </p>
            </ArrivalNotice>
          ) : null}
        </>
      }
    />
  )
}

function BookingDetail({
  detail,
  locale,
  m,
  back,
  notices,
}: {
  detail: Detail
  locale: StaffLocale
  m: StaffMessages
  back: ReactNode
  notices: ReactNode
}) {
  // S9: what this person may do here, as the booking's answer says it.
  const may = canDo(detail.can)
  const { booking, customer, quote } = detail
  const payment = paymentNote(detail)
  const notGiven = <span className="text-text-muted">{m.booking.notGiven}</span>
  // S8: an anonymised customer names nobody; a contact erased says when, never "not given".
  const anonymisedAt = customer.anonymisedAt
  const name = anonymisedAt === null ? customerName(customer) : m.customer.anonymisedTitle
  const erased =
    customer.contactErasedAt === null ? null : <ErasedOn at={customer.contactErasedAt} />
  // Here, not in the dialogs: the booking re-renders as it is now once they finish.
  const [outcome, setOutcome] = useState<LifecycleOutcome | null>(null)
  // OPS-1: each move is its own outcome, so a second one is said again.
  const [moved, setMoved] = useState(0)
  return (
    <>
      {/* Who, what and when, where it stands, and the next step at the counter, quieter steps first. */}
      <PageHeader
        back={back}
        title={format(m.booking.title, { code: booking.code })}
        status={<StatusBadge status={booking.status} hold={booking.hold} />}
        actions={
          <>
            <MoveBookingAction detail={detail} onMoved={() => setMoved((count) => count + 1)} />
            <LifecycleActions detail={detail} onDone={setOutcome} />
            <PaperAction detail={detail} />
            <RentalActions detail={detail} />
          </>
        }
        meta={[
          name,
          localized(booking.category.name, locale),
          `${formatDateTime(locale, booking.startsAt)} → ${formatDateTime(locale, booking.endsAt)}`,
          // S9: the reference its customer knows, for one carried over from the old site.
          ...(booking.oldSiteReference
            ? [format(m.booking.oldSite, { reference: booking.oldSiteReference })]
            : []),
        ].join(' · ')}
      >
        <CloseHint detail={detail} />
        <HoldNotice hold={booking.hold} />
        <MoneyStrip money={detail.money} className="mt-2" />
      </PageHeader>
      {notices}
      <LifecycleNotices outcome={outcome} />
      {moved ? (
        <OutcomeNotice key={moved}>
          <p>{m.timeline.moved}</p>
        </OutcomeNotice>
      ) : null}
      {/* S9: the trips first, where a booking has any: for whoever drives, they are the job. */}
      <BookingTrips detail={detail} may={may} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title={m.booking.summary}>
          <Facts
            items={[
              [
                m.booking.pickup,
                `${formatDateTime(locale, booking.startsAt)} · ${placeName(booking.pickup, locale)}`,
              ],
              [
                m.booking.return,
                `${formatDateTime(locale, booking.endsAt)} · ${placeName(booking.return, locale)}`,
              ],
              [m.booking.category, localized(booking.category.name, locale)],
              [m.booking.vehicle, booking.vehicle?.plate ?? m.today.unassigned],
              [m.booking.source, m.sources[booking.source]],
              ...(booking.oldSiteReference
                ? ([[m.newBooking.oldSite.reference, booking.oldSiteReference]] as const)
                : []),
              [
                m.booking.total,
                <span key="total" className="figures font-bold">
                  {formatEuro(booking.totalCents, locale)} ·{' '}
                  {plural(locale, m.booking.rentalDays, booking.billableDays)}
                </span>,
              ],
            ]}
          />
          {detail.confirmedAt ? (
            <p className="text-sm text-text-muted">
              {format(m.booking.confirmedAt, { date: formatDateTime(locale, detail.confirmedAt) })}
            </p>
          ) : null}
          {detail.ending ? <EndingFacts ending={detail.ending} /> : null}
          {/* S9: no automatic e-mail, and the old site's terms: the counter's to carry out. */}
          {booking.oldSiteReference ? (
            <Notice tone="info">
              <p>{format(m.booking.oldSiteNote, { reference: booking.oldSiteReference })}</p>
            </Notice>
          ) : null}
        </Section>

        <Section title={m.booking.customer}>
          <Facts
            items={[
              [
                m.booking.name,
                may('openCustomer') ? (
                  <Link
                    key="customer"
                    to="/customers/$customerId"
                    params={{ customerId: customer.id }}
                    className="touch-target"
                  >
                    {name}
                  </Link>
                ) : (
                  name
                ),
              ],
              // Anonymised, nothing else is left to show: the note below says what went.
              ...(anonymisedAt === null
                ? ([
                    [
                      m.booking.email,
                      erased ??
                        (customer.email ? (
                          <a key="email" href={`mailto:${customer.email}`} className="touch-target">
                            <EmailText address={customer.email} />
                          </a>
                        ) : (
                          notGiven
                        )),
                    ],
                    [
                      m.booking.phone,
                      erased ??
                        (customer.phone ? (
                          <a
                            key="phone"
                            href={`tel:${customer.phone}`}
                            className="figures touch-target"
                          >
                            {customer.phone}
                          </a>
                        ) : (
                          notGiven
                        )),
                    ],
                    [
                      m.booking.nationality,
                      customer.nationality ? countryName(locale, customer.nationality) : notGiven,
                    ],
                    [
                      m.booking.birthDate,
                      customer.birthDate
                        ? formatDate(locale, customer.birthDate, 'short')
                        : notGiven,
                    ],
                  ] as const)
                : []),
            ]}
          />
          {anonymisedAt === null ? null : <AnonymisedNote anonymisedAt={anonymisedAt} />}
        </Section>

        <Section title={m.booking.driver}>
          <div>
            <StatusChip
              tone={
                booking.eligibility === 'eligible'
                  ? 'success'
                  : booking.eligibility === 'ineligible'
                    ? 'danger'
                    : 'warning'
              }
            >
              {m.eligibility[booking.eligibility]}
            </StatusChip>
          </div>
          {detail.eligibilityReasons.length > 0 ? (
            <>
              <p className="font-medium">{m.booking.toCheck}</p>
              <ul className="flex list-disc flex-col gap-1 ps-6">
                {detail.eligibilityReasons.map((reason) => (
                  <li key={reason.code}>{reasonText(reason, locale, m)}</li>
                ))}
              </ul>
            </>
          ) : null}
          <h3 className="text-lg">{m.booking.documents}</h3>
          <DocumentList documents={detail.documents} erased={detail.erasedDocuments} can={may} />
        </Section>

        {/* Q-162: the second driver, beside the renter it drives with. */}
        <SecondDriverSection detail={detail} />

        <Section title={m.booking.price}>
          {quote ? (
            <table className="w-full">
              <tbody>
                {quote.lines.map((line) => (
                  <tr key={`${line.kind}-${line.code}`} className="border-b border-rule">
                    <th scope="row" className="py-2 text-left font-regular">
                      {lineLabel(line, locale, m)}
                      {line.days !== null ? (
                        <span className="text-sm text-text-muted">
                          {' '}
                          · {plural(locale, MESSAGES[locale].common.days, line.days)}
                        </span>
                      ) : null}
                    </th>
                    <td className="figures whitespace-nowrap py-2 text-right">
                      {formatEuro(line.totalCents, locale)}
                    </td>
                  </tr>
                ))}
                {/* Q-162: the second driver's fee, fixed when they were added, is in the total. */}
                {detail.secondDriver ? (
                  <tr className="border-b border-rule">
                    <th scope="row" className="py-2 text-left font-regular">
                      {m.secondDriver.feeRow}
                    </th>
                    <td className="figures whitespace-nowrap py-2 text-right">
                      {formatEuro(detail.secondDriver.feeCents, locale)}
                    </td>
                  </tr>
                ) : null}
                {/*
                  A manual price (S9: every old-site booking whose agreed price differs) costs
                  the booking's total: the lines' sum is the list price it replaced, never a
                  second bold total beside the one the Conto charges.
                */}
                {detail.priceOverrideReason ? (
                  <tr className="border-b border-rule">
                    <th scope="row" className="py-2 text-left font-regular">
                      {m.booking.listPrice}
                    </th>
                    <td className="figures whitespace-nowrap py-2 text-right">
                      {formatEuro(quote.totalCents, locale)}
                    </td>
                  </tr>
                ) : null}
                <tr>
                  <th scope="row" className="py-2 text-left">
                    {m.booking.total}
                  </th>
                  <td className="figures whitespace-nowrap py-2 text-right font-bold">
                    {formatEuro(
                      detail.priceOverrideReason
                        ? booking.totalCents
                        : quote.totalCents + (detail.secondDriver?.feeCents ?? 0),
                      locale,
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <Notice tone="info">
              <p>{m.booking.quoteUnreadable}</p>
            </Notice>
          )}
          {detail.priceOverrideReason && quote ? (
            <Notice tone="info">
              <p>
                {format(m.booking.priceOverridden, {
                  quoted: formatEuro(quote.totalCents, locale),
                  reason: detail.priceOverrideReason,
                })}
              </p>
            </Notice>
          ) : null}
          <h3 className="text-lg">{m.booking.payment}</h3>
          {payment === 'linkPending' || payment === 'fineFeeLinkPending' ? (
            <PendingLine>
              {m.booking[payment === 'linkPending' ? 'paymentLinkPending' : payment]}
            </PendingLine>
          ) : (
            <p>{m.booking[payment === 'linkReady' ? 'payAtCounter' : payment]}</p>
          )}
          {payment === 'linkReady' || payment === 'fineFeeLink' ? (
            <PaymentLink detail={detail} />
          ) : null}
          <p className="text-sm text-text-muted">
            {detail.termsVersion
              ? format(m.booking.terms, { version: detail.termsVersion })
              : detail.rental
                ? format(m.booking.termsSignedAtCheckout, {
                    version: detail.rental.termsVersion ?? '—',
                  })
                : // Signed on the iPad before the hand-over: the contract quotes the terms.
                  detail.contract.signedAt && detail.contract.termsVersion
                  ? format(m.booking.terms, { version: detail.contract.termsVersion })
                  : // S9: the old site's terms, until the contract signed here quotes ours.
                    booking.source === 'old_site'
                    ? m.booking.oldSiteTerms
                    : m.booking.noTerms}
          </p>
          <ContractLine detail={detail} />
        </Section>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        {detail.rental ? <RentalBlock rental={detail.rental} /> : null}
        <MoneyBlock detail={detail} />
        <DepositBlock detail={detail} />
      </div>
      {detail.rental ? (
        <Section title={m.inspection.title}>
          <InspectionPanel code={detail.booking.code} readOnly can={may} />
        </Section>
      ) : null}
      {/* S6 (CMP-2): the fines this booking's rental was matched to. */}
      {detail.fines.length > 0 ? (
        <Section title={m.fines.mentions.title}>
          <FineMentions fines={detail.fines} withCode={false} />
        </Section>
      ) : null}
      {detail.notes ? (
        <Section title={m.booking.notes}>
          <p className="whitespace-pre-line">{detail.notes}</p>
        </Section>
      ) : null}
    </>
  )
}

/**
 * CTR-2 and CTR-5: where the contract stands, and the signed PDF once the
 * worker made it. S8: a booking naming someone anonymised opens it only for
 * customers.privacy, by the PDF route's own rule; anyone else is told why.
 */
function ContractLine({ detail }: { detail: Detail }) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const { contract, booking } = detail
  // S9: the signed copy is the office's; a trip reads where the contract stands, not its PDF.
  const pdf = canDo(detail.can)('contractPdf')
  const opens = !contract.ownerOnly || can(access, 'customers.privacy')
  // A booking that never reached the counter has nothing to say about its contract.
  if (contract.status === 'none' && booking.status !== 'confirmed') return null
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-lg">{m.booking.contract.title}</h3>
      <p>
        {contract.status === 'signed' && contract.signedAt
          ? format(m.booking.contract.signed, { date: formatDateTime(locale, contract.signedAt) })
          : m.booking.contract[contract.status === 'signed' ? 'prepared' : contract.status]}
      </p>
      {contract.status === 'signed' && pdf ? (
        !contract.pdf ? (
          <p className="text-sm text-text-muted">{m.booking.contract.pdfPending}</p>
        ) : opens ? (
          <>
            <ContractPdfLink code={booking.code} />
            {contract.ownerOnly ? (
              <p className="text-sm text-text-muted">{m.booking.contract.viewLogged}</p>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-text-muted">{m.booking.contract.ownerOnly}</p>
        )
      ) : null}
    </div>
  )
}

/**
 * OPS-1 and OPS-2: the next step at the counter, for the roles that take it.
 * S9: and for whoever drives its trip, the hand-over on a delivery, the
 * return on a collection.
 */
function RentalActions({ detail }: { detail: Detail }) {
  const { messages: m } = useLocale()
  const may = canDo(detail.can)
  const { booking } = detail
  if (booking.status === 'confirmed' && may('handOver')) {
    return (
      <Link
        to="/bookings/$code/checkout"
        params={{ code: booking.code }}
        className={buttonVariants({ variant: 'staff' })}
      >
        {m.rental.handOver}
      </Link>
    )
  }
  if (booking.status === 'checked_out' && may('return')) {
    return (
      <Link
        to="/bookings/$code/return"
        params={{ code: booking.code }}
        className={buttonVariants({ variant: 'staff' })}
      >
        {m.rental.takeBack}
      </Link>
    )
  }
  return null
}

/**
 * S11: the Owner types in a rental kept on paper, while the settings allow it
 * and the booking still has a step that paper may have taken for it.
 */
function PaperAction({ detail }: { detail: Detail }) {
  const { messages: m } = useLocale()
  const { booking } = detail
  if (!canDo(detail.can)('reenter') || !detail.reentry) return null
  if (!['confirmed', 'checked_out', 'returned'].includes(booking.status)) return null
  return (
    <Link
      to="/bookings/$code/paper"
      params={{ code: booking.code }}
      className={buttonVariants({ variant: 'outline' })}
    >
      {m.paper.open}
    </Link>
  )
}

function RentalBlock({ rental }: { rental: NonNullable<Detail['rental']> }) {
  const { locale, messages: m } = useLocale()
  const facts: [ReactNode, ReactNode][] = [
    [
      m.rental.kmOut,
      <span key="km-out" className="figures">
        {rental.kmOut}
      </span>,
    ],
    [m.rental.fuelOut, fuelLabel(rental.fuelOut, m)],
  ]
  if (rental.kmIn !== null) {
    facts.push(
      [
        m.rental.kmIn,
        <span key="km-in" className="figures">
          {rental.kmIn}
        </span>,
      ],
      [
        m.rental.kmDriven,
        <span key="km" className="figures">
          {rental.kmIn - rental.kmOut}
        </span>,
      ],
    )
  }
  if (rental.fuelIn !== null) facts.push([m.rental.fuelIn, fuelLabel(rental.fuelIn, m)])
  if (rental.contractReference) facts.push([m.rental.contract, rental.contractReference])
  if (rental.cargosReceipt) facts.push([m.rental.cargos, rental.cargosReceipt])
  return (
    <Section title={m.rental.title}>
      <p>
        {format(m.rental.checkedOut, {
          date: formatDateTime(locale, rental.checkedOutAt),
          name: rental.checkedOutBy,
        })}
      </p>
      {/* S11: typed in from paper; the times above are the sheet's. */}
      {rental.paper ? (
        <p className="text-sm text-text-muted">
          {format(m.paper.handedOver, {
            sheet: rental.paper.sheet,
            date: formatDateTime(locale, rental.paper.enteredAt),
            reason: rental.paper.reason,
          })}
        </p>
      ) : null}
      <p>
        {rental.returnedAt
          ? format(m.rental.returned, {
              date: formatDateTime(locale, rental.returnedAt),
              name: rental.returnedBy ?? '',
            })
          : m.rental.notReturned}
      </p>
      {rental.returnPaper ? (
        <p className="text-sm text-text-muted">
          {format(m.paper.returned, {
            date: formatDateTime(locale, rental.returnPaper.enteredAt),
            reason: rental.returnPaper.reason,
          })}
        </p>
      ) : null}
      <Facts items={facts} />
      {rental.overrideReason ? (
        <Notice tone="warning">
          <p>{format(m.rental.override, { reason: rental.overrideReason })}</p>
        </Notice>
      ) : null}
      {rental.returnNotes ? (
        <>
          <h3 className="text-lg">{m.rental.notes}</h3>
          <p className="whitespace-pre-line">{rental.returnNotes}</p>
        </>
      ) : null}
    </Section>
  )
}

/** PAY-2 and PAY-3: the deposit, from the hand-over until it is released or partly kept. */
function DepositBlock({ detail }: { detail: Detail }) {
  const { messages: m } = useLocale()
  const { booking, deposit } = detail
  // A booking that never reached the counter has no deposit to show.
  if (!deposit.current && !['confirmed', 'checked_out', 'returned'].includes(booking.status)) {
    return null
  }
  return (
    <Section title={m.deposit.title}>
      <DepositPanel
        code={booking.code}
        deposit={deposit}
        status={booking.status}
        balanceCents={detail.money.balanceCents}
        endsAt={booking.endsAt}
        can={canDo(detail.can)}
      />
    </Section>
  )
}

/**
 * RES-4 and J2: a link for a phone or WhatsApp customer to pay the balance on
 * the website. E-mailed to the address on the booking, or made for copying
 * into a WhatsApp message (iRevenueChat, NTF-2, is not built). The server
 * decides the amount when the customer pays; this only asks for the link.
 */
function PaymentLink({ detail }: { detail: Detail }) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState<'email' | 'copy' | null>(null)
  const noEmailId = useId()
  const [made, setMade] = useState<ApiSchemas['PaymentLinkCreated'] | null>(null)
  const [copied, setCopied] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  if (!canDo(detail.can)('payment')) return null
  const code = detail.booking.code
  const email = detail.customer.email
  const lastSent = detail.payment.link?.lastSent ?? null

  async function make(byEmail: boolean) {
    setProblem(null)
    setCopied(false)
    setBusy(byEmail ? 'email' : 'copy')
    // Each press is a new link; a lost answer is sent again with its own key.
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/payment-link', {
        params: { path: { code }, header: { 'idempotency-key': newIdempotencyKey() } },
        body: { email: byEmail },
      }),
    )
    setBusy(null)
    if (result.ok) {
      setMade(result.data)
      await queryClient.invalidateQueries({ queryKey: ['booking', code] })
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      // No clipboard (an older browser, or permission refused): the field stays selectable.
      setCopied(false)
    }
  }

  return (
    <SubPanel title={m.booking.paymentLink}>
      <p>
        {format(m.booking.paymentLinkIntro, {
          amount: formatEuro(detail.money.balanceCents, locale),
        })}
      </p>
      {lastSent ? (
        <p className="text-sm text-text-muted">
          {format(
            lastSent.emailed ? m.booking.paymentLinkLastEmailed : m.booking.paymentLinkLastMade,
            {
              date: formatDateTime(locale, lastSent.at),
            },
          )}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="staff"
          busy={busy === 'email'}
          disabled={!email || (busy !== null && busy !== 'email')}
          // Without an address it cannot be sent: the line under the buttons says why.
          aria-describedby={email ? undefined : noEmailId}
          onClick={() => void make(true)}
        >
          {m.booking.paymentLinkEmail}
        </Button>
        <Button
          variant="outline"
          className="rounded-sm"
          busy={busy === 'copy'}
          disabled={busy !== null && busy !== 'copy'}
          onClick={() => void make(false)}
        >
          {m.booking.paymentLinkMake}
        </Button>
      </div>
      {!email ? (
        <p id={noEmailId} className="text-sm text-text-muted">
          {m.booking.paymentLinkNoEmail}
        </p>
      ) : null}
      {made ? (
        <>
          <Notice tone="success" live="polite">
            <p>
              {format(made.emailed ? m.booking.paymentLinkEmailed : m.booking.paymentLinkMade, {
                date: formatDateTime(locale, made.expiresAt),
              })}
            </p>
          </Notice>
          <Field label={m.booking.paymentLinkField}>
            {(control) => (
              <div className="flex flex-wrap items-center gap-3">
                <StaffInput
                  {...control}
                  readOnly
                  value={made.url}
                  className="min-w-0 flex-1"
                  onFocus={(event) => event.currentTarget.select()}
                />
                <Button
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => void copy(made.url)}
                >
                  {m.booking.paymentLinkCopy}
                </Button>
                {copied ? (
                  <span role="status" className="text-sm text-text-muted">
                    {m.booking.paymentLinkCopied}
                  </span>
                ) : null}
              </div>
            )}
          </Field>
        </>
      ) : null}
      <ProblemNotice problem={problem} />
    </SubPanel>
  )
}

/** What the booking costs, what was added at return, what was paid at the counter. */
function MoneyBlock({ detail }: { detail: Detail }) {
  const { locale, messages: m } = useLocale()
  const may = canDo(detail.can)
  const { money, ending } = detail
  const open = TAKES_COUNTER_PAYMENT.includes(detail.booking.status)
  // S3: a cancelled or no-show booking owes its fee, not its total.
  const ended = ENDED_WITHOUT_RENTAL.includes(detail.booking.status) && money.feeCents !== null
  // Here, not in the form: a payment that settles the account removes the form.
  const [outcome, setOutcome] = useState<PaymentOutcome | null>(null)
  return (
    <Section title={m.money.title}>
      <table className="w-full">
        <tbody>
          <tr className="border-b border-rule">
            <th scope="row" className="py-2 text-left font-regular">
              {ended ? m.money.fee : m.money.total}
              {ended && ending?.feeRule ? (
                <span className="text-sm text-text-muted">
                  {' '}
                  ·{' '}
                  {format(m.money.feeRules[ending.feeRule], {
                    version: ending.termsVersion ?? '—',
                    note: ending.feeNote ?? '',
                  })}
                </span>
              ) : null}
            </th>
            <td className="figures whitespace-nowrap py-2 text-right">
              {formatEuro(ended ? (money.feeCents ?? 0) : money.totalCents, locale)}
            </td>
          </tr>
          {detail.charges.map((charge) => (
            <tr
              key={`${charge.kind}-${charge.createdAt}-${charge.description}`}
              className="border-b border-rule"
            >
              <th scope="row" className="py-2 text-left font-regular">
                {m.chargeKinds[charge.kind]}
                <span className="text-sm text-text-muted"> · {charge.description}</span>
              </th>
              <td className="figures whitespace-nowrap py-2 text-right">
                {formatEuro(charge.amountCents, locale)}
              </td>
            </tr>
          ))}
          <tr className="border-b border-rule">
            <th scope="row" className="py-2 text-left font-regular">
              {m.money.paid}
            </th>
            <td className="figures whitespace-nowrap py-2 text-right">
              − {formatEuro(money.paidCents, locale)}
            </td>
          </tr>
          {money.refundedCents > 0 ? (
            <tr className="border-b border-rule">
              <th scope="row" className="py-2 text-left font-regular">
                {m.money.refunded}
              </th>
              <td className="figures whitespace-nowrap py-2 text-right">
                + {formatEuro(money.refundedCents, locale)}
              </td>
            </tr>
          ) : null}
          {money.lostInDisputesCents > 0 ? (
            <tr className="border-b border-rule">
              <th scope="row" className="py-2 text-left font-regular">
                {m.money.lostInDisputes}
              </th>
              <td
                className="figures whitespace-nowrap py-2 text-right"
                data-testid="lost-in-disputes"
              >
                + {formatEuro(money.lostInDisputesCents, locale)}
              </td>
            </tr>
          ) : null}
          <tr>
            <th scope="row" className="py-2 text-left">
              {money.balanceCents > 0
                ? m.money.balance
                : money.balanceCents < 0
                  ? m.money.credit
                  : m.money.settled}
            </th>
            <td
              className="figures whitespace-nowrap py-2 text-right font-bold"
              data-testid="balance"
            >
              {formatEuro(Math.abs(money.balanceCents), locale)}
            </td>
          </tr>
        </tbody>
      </table>
      {ended ? (
        <p className="text-sm text-text-muted">
          {format(m.money.endedTotal, { amount: formatEuro(money.totalCents, locale) })}
        </p>
      ) : null}
      {money.balanceCents < 0 ? (
        <Notice tone="warning">
          <p>{m.money.creditHint}</p>
        </Notice>
      ) : null}
      <h3 className="text-lg">{m.money.payments}</h3>
      {detail.payments.length === 0 ? (
        <p className="text-text-muted">{m.money.noPayments}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {detail.payments.map((payment) => (
            <li key={payment.id} className="flex flex-col gap-1">
              <span className="figures">
                {format(m.money.paymentLine, {
                  amount: formatEuro(payment.amountCents, locale),
                  method: m.paymentMethods[payment.method],
                  // S11: money typed in from paper, at the time it was taken.
                  date: formatDateTime(locale, payment.occurredAt ?? payment.createdAt),
                  name: payment.recordedBy ?? m.money.onlineBy,
                })}
                {payment.reference ? ` · ${payment.reference}` : ''}
              </span>
              {payment.occurredAt ? (
                <span className="text-sm text-text-muted">
                  {format(m.paper.paidOnPaper, {
                    date: formatDateTime(locale, payment.createdAt),
                  })}
                </span>
              ) : null}
              {/* S6 (CMP-3): the document it was given, or that it has none yet. */}
              <MoneyDocuments
                documents={detail.fiscalDocuments}
                here={payment.fiscalDocumentHere}
                mayType={may('fiscalDocument')}
                target={{
                  money: 'payment',
                  id: payment.id,
                  method: payment.method,
                  amountCents: payment.amountCents,
                  // A7: the receipt was printed when the money was taken.
                  at: payment.occurredAt ?? payment.createdAt,
                  code: detail.booking.code,
                }}
              />
            </li>
          ))}
        </ul>
      )}
      {detail.disputes.length > 0 ? <Disputes disputes={detail.disputes} /> : null}
      <RefundPanel detail={detail} />
      {/* The form empties, or goes with what was owed: what became of the payment takes the focus. */}
      {outcome?.kind === 'recorded' ? (
        <OutcomeFocus key="recorded">
          <Notice tone="success">
            <p>{m.payment.done}</p>
          </Notice>
          {outcome.onlinePageOpenUntil ? (
            <Notice tone="warning">
              <p>
                {format(m.payment.pageOpen, {
                  time: formatTime(locale, outcome.onlinePageOpenUntil),
                })}
              </p>
            </Notice>
          ) : null}
        </OutcomeFocus>
      ) : null}
      {outcome?.kind === 'paidOnline' ? (
        <OutcomeNotice key="paidOnline" tone="warning">
          <p>{m.payment.paidOnline}</p>
        </OutcomeNotice>
      ) : null}
      {open && money.balanceCents > 0 && may('payment') ? (
        <PaymentForm
          code={detail.booking.code}
          balanceCents={money.balanceCents}
          may={may}
          onOutcome={setOutcome}
        />
      ) : null}
    </Section>
  )
}

/**
 * S5: the disputes of the booking's payments, as Stripe reports them: what
 * the bank is deciding, by when the Owner answers in Stripe's Dashboard, and
 * what it means at the counter. Read-only: the answer is given at Stripe.
 */
function Disputes({ disputes }: { disputes: Detail['disputes'] }) {
  const { locale, messages: m } = useLocale()
  const reasons: Readonly<Record<string, string>> = m.disputes.reasons
  // A status or reason Stripe adds after this app is said in words; its code is the Owner's (C5).
  const statusOf = (dispute: Detail['disputes'][number]) =>
    dispute.known
      ? m.disputes.status[dispute.status as keyof typeof m.disputes.status]
      : m.disputes.statusUnknown
  const codesOf = (dispute: Detail['disputes'][number]) => [
    ...(dispute.known ? [] : [format(m.app.stripeCode, { code: dispute.status })]),
    ...(reasons[dispute.reason] ? [] : [format(m.app.stripeCode, { code: dispute.reason })]),
  ]
  return (
    <section aria-labelledby="disputes-title" className="flex flex-col gap-2">
      <h3 id="disputes-title" className="text-lg">
        {m.disputes.title}
      </h3>
      <ul className="flex flex-col gap-3">
        {disputes.map((dispute) => (
          <li key={dispute.id} className="flex flex-col gap-1" data-testid="dispute">
            <span className="flex flex-wrap items-center gap-2">
              {/* Lost: red; still being decided: orange; won or closed: simply ended. */}
              <StatusChip tone={dispute.returned ? 'danger' : dispute.open ? 'warning' : 'neutral'}>
                {statusOf(dispute)}
              </StatusChip>
              <span className="figures">
                {format(m.disputes.line, {
                  amount: formatEuro(dispute.amountCents, locale),
                  reason: reasons[dispute.reason] ?? m.disputes.otherReason,
                  date: formatDateTime(locale, dispute.openedAt),
                })}
              </span>
            </span>
            {dispute.needsAnswer && dispute.evidenceDueBy ? (
              <span className="font-medium">
                {format(m.disputes.answerBy, {
                  date: formatDateTime(locale, dispute.evidenceDueBy),
                })}
              </span>
            ) : null}
            <TechnicalLines lines={codesOf(dispute)} />
          </li>
        ))}
      </ul>
      {disputes.some((dispute) => dispute.open) ? (
        <Notice tone="warning">
          <p>{m.disputes.openHint}</p>
        </Notice>
      ) : null}
      {disputes.some((dispute) => dispute.returned) ? (
        <Notice tone="info">
          <p>{m.disputes.returnedHint}</p>
        </Notice>
      ) : null}
    </section>
  )
}

/** What became of the last payment taken at the counter, shown beside the account. */
type PaymentOutcome =
  | { kind: 'recorded'; onlinePageOpenUntil: string | null }
  /** Q-78: refused, the customer had just paid online; that payment is now in the list. */
  | { kind: 'paidOnline' }

/** PAY-1, by hand: what was taken at the counter, never more than what is owed. */
function PaymentForm({
  code,
  balanceCents,
  may,
  onOutcome,
}: {
  code: string
  balanceCents: number
  /**
   * S9: the booking's own answer. On a trip there is no till: a receipt is
   * typed later at the office, and a bank transfer is the office's to follow.
   */
  may: CanDo
  onOutcome: (outcome: PaymentOutcome | null) => void
}) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const [method, setMethod] = useState<CounterPaymentMethod | ''>('')
  const [amount, setAmount] = useState(() => centsForInput(balanceCents, locale))
  const [reference, setReference] = useState('')
  // S5: cash goes into an open drawer.
  const [cashSessionId, setCashSessionId] = useState<string | null>(null)
  // S6 (CMP-3): what the till printed for it, if typed now.
  const [fiscalDraft, setFiscalDraft] = useState<DocumentDraft>(() =>
    newDraft('payment', '', { point: rememberedPoint() }),
  )
  const [documentMissing, setDocumentMissing] = useState<'point' | 'printed' | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const lastSent = useRef<{ body: string; key: string } | null>(null)
  const cents = parseEuroCents(amount)
  // UI-1c: what still stops the payment, in words; none left means it can be sent.
  const why = m.payment.why
  const reasons = [
    ...(method === '' ? [why.method] : []),
    ...(cents === null || cents <= 0 ? [why.amount] : []),
    ...(cents !== null && cents > balanceCents
      ? [format(why.overBalance, { amount: formatEuro(balanceCents, locale) })]
      : []),
    ...(method === 'cash' && cashSessionId === null ? [why.drawer] : []),
  ]
  const ready = reasons.length === 0
  const reasonsId = useId()

  async function submit(event: FormEvent) {
    event.preventDefault()
    // `ready` says the same; the types learn it only from the conditions themselves.
    if (!ready || method === '' || cents === null) return
    const fiscal = documentBody(fiscalDraft, { pointFromDrawer: method === 'cash' })
    if (!fiscal.ok) {
      setDocumentMissing(fiscal.missing)
      return
    }
    setDocumentMissing(null)
    setProblem(null)
    onOutcome(null)
    const body = {
      method,
      amountCents: cents,
      ...(reference.trim() ? { reference: reference.trim() } : {}),
      ...(method === 'cash' && cashSessionId ? { cashSessionId } : {}),
      ...(fiscal.document ? { fiscalDocument: fiscal.document } : {}),
    }
    // A retry of the same payment replays; a different one gets its own key.
    const serialized = JSON.stringify(body)
    if (lastSent.current?.body !== serialized) {
      lastSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/payments', {
        params: { path: { code }, header: { 'idempotency-key': lastSent.current.key } },
        body,
      }),
    )
    setBusy(false)
    if (result.ok) {
      lastSent.current = null
      onOutcome({ kind: 'recorded', onlinePageOpenUntil: result.data.onlinePageOpenUntil })
      setMethod('')
      setReference('')
      setFiscalDraft(newDraft('payment', '', { point: rememberedPoint() }))
      setAmount(centsForInput(Math.max(0, result.data.money.balanceCents), locale))
      // The booking, and Fiscale's list when no document came with it.
      await refreshFiscal(queryClient, code)
      return
    }
    const problemCode = result.problem.kind === 'api' ? result.problem.code : null
    if (problemCode === 'paid_online_meanwhile') {
      // Nothing was taken, and the online payment the server found is now in the account.
      lastSent.current = null
      onOutcome({ kind: 'paidOnline' })
      await queryClient.invalidateQueries({ queryKey: ['booking', code] })
      return
    }
    if (problemCode === 'payment_processing') {
      await queryClient.invalidateQueries({ queryKey: ['booking', code] })
    }
    await refreshDrawersAfter(queryClient, result.problem)
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  return (
    <SubPanel title={m.payment.title}>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <ChoiceChips
          name="payment-method"
          label={m.payment.method}
          labelHidden
          options={COUNTER_PAYMENT_METHODS.filter(
            (value) => value !== 'bank_transfer' || may('bankTransfer'),
          ).map((value) => ({
            value,
            label: m.paymentMethods[value],
          }))}
          value={method}
          onChange={(value) => {
            setMethod(value)
            // Until a number is typed, the kind follows the method (a transfer is invoiced).
            setFiscalDraft((draft) =>
              draft.number.trim() ? draft : withKind(draft, defaultKind('payment', value)),
            )
          }}
        />
        {method === 'cash' ? (
          <DrawerField name={`payment-drawer-${code}`} code={code} onChange={setCashSessionId} />
        ) : null}
        <div className={FIELD_ROW}>
          <Field
            className={FIELD}
            label={m.payment.amount}
            required
            error={amount.trim() !== '' && cents === null ? m.payment.amountInvalid : null}
          >
            {(control) => (
              <StaffInput
                {...control}
                inputMode="decimal"
                autoComplete="off"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            )}
          </Field>
          <Field
            className={FIELD}
            label={m.payment.reference}
            hint={m.payment.referenceHint}
            optionalLabel={m.newBooking.optional}
          >
            {(control) => (
              <StaffInput
                {...control}
                maxLength={100}
                autoComplete="off"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            )}
          </Field>
        </div>
        {may('fiscalDocument') ? (
          <FiscalFields
            name={`payment-fiscal-${code}`}
            money="payment"
            draft={fiscalDraft}
            onChange={setFiscalDraft}
            pointFromDrawer={method === 'cash'}
            printed="onRequest"
            missing={documentMissing}
          />
        ) : null}
        <ProblemNotice problem={problem} />
        <div className="flex flex-col gap-2">
          <div>
            <Button
              type="submit"
              variant="staff"
              busy={busy}
              disabled={!ready && !busy}
              aria-describedby={ready || busy ? undefined : reasonsId}
            >
              {m.payment.submit}
            </Button>
          </div>
          {busy ? null : <ReasonList id={reasonsId} reasons={reasons} />}
        </div>
      </form>
    </SubPanel>
  )
}
